import { useEffect, useState } from 'react';
import { Loader2, Mail, MessageSquare, Plug, Check } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

type EmailAccount = {
  id: string;
  provider: 'gmail' | 'outlook';
  email_address: string;
  status: string;
};

type PhoneNumber = {
  id: string;
  phone_e164: string;
  forward_to_e164: string | null;
  forward_enabled: boolean;
  status: string;
};

export function CommunicationsCard() {
  const { userId, dealershipId, mode } = useAuth();
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState<EmailAccount | null>(null);
  const [phone, setPhone] = useState<PhoneNumber | null>(null);
  const [forwardTo, setForwardTo] = useState('');
  const [savingForward, setSavingForward] = useState(false);

  useEffect(() => {
    if (mode !== 'real' || !userId || !dealershipId) {
      setLoading(false);
      return;
    }
    let active = true;
    (async () => {
      const [e, p] = await Promise.all([
        supabase.from('rep_email_accounts').select('id, provider, email_address, status').eq('rep_id', userId).maybeSingle(),
        supabase.from('rep_phone_numbers').select('id, phone_e164, forward_to_e164, forward_enabled, status').eq('rep_id', userId).maybeSingle(),
      ]);
      if (!active) return;
      setEmail((e.data as EmailAccount) ?? null);
      setPhone((p.data as PhoneNumber) ?? null);
      setForwardTo((p.data as PhoneNumber)?.forward_to_e164 ?? '');
      setLoading(false);
    })();
    return () => { active = false; };
  }, [mode, userId, dealershipId]);

  const connectEmail = async (provider: 'gmail' | 'outlook') => {
    const { data, error } = await supabase.functions.invoke('connect-rep-email', {
      body: { provider, dealership_id: dealershipId },
    });
    if (error || (data as any)?.needs_setup) {
      toast.error((data as any)?.error || `${provider === 'gmail' ? 'Gmail' : 'Outlook'} integration is being set up. Try again shortly.`);
      return;
    }
    if ((data as any)?.auth_url) {
      window.location.href = (data as any).auth_url;
    }
  };

  const saveForward = async () => {
    if (!phone) return;
    setSavingForward(true);
    const { error } = await supabase
      .from('rep_phone_numbers')
      .update({ forward_to_e164: forwardTo || null })
      .eq('id', phone.id);
    setSavingForward(false);
    if (error) {
      toast.error('Could not save forwarding number');
      return;
    }
    toast.success('Forwarding number updated');
    setPhone({ ...phone, forward_to_e164: forwardTo || null });
  };

  if (mode !== 'real') return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Plug className="h-4 w-4 text-primary" /> Communications
        </CardTitle>
        <CardDescription>
          Send email and text from your own identity — customers reply directly to you. PullOver just logs the conversation.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* Email */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Email account</span>
                {email && <Badge variant="default" className="ml-auto"><Check className="h-3 w-3 mr-1" />Connected</Badge>}
              </div>
              {email ? (
                <div className="text-sm text-muted-foreground">
                  {email.email_address}{' '}
                  <span className="text-xs">({email.provider === 'gmail' ? 'Gmail' : 'Outlook'})</span>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => connectEmail('gmail')}>
                    Connect Gmail
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => connectEmail('outlook')}>
                    Connect Outlook
                  </Button>
                </div>
              )}
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Texting number</span>
                {phone && <Badge variant="default" className="ml-auto"><Check className="h-3 w-3 mr-1" />Assigned</Badge>}
              </div>
              {phone ? (
                <>
                  <div className="text-sm text-muted-foreground">{phone.phone_e164}</div>
                  <div className="space-y-1.5">
                    <Label htmlFor="forward" className="text-xs">Forward inbound texts to my cell</Label>
                    <div className="flex gap-2">
                      <Input
                        id="forward"
                        value={forwardTo}
                        onChange={(e) => setForwardTo(e.target.value)}
                        placeholder="+15195551234"
                      />
                      <Button
                        size="sm"
                        onClick={saveForward}
                        disabled={savingForward || forwardTo === (phone.forward_to_e164 ?? '')}
                      >
                        {savingForward ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Ask your manager to assign you a PullOver texting number.
                </p>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}